import React, { Dispatch, FC, useCallback, useEffect, useMemo, useState } from 'react';
import { getDownloadURL, getMetadata, getStorage, ref } from 'firebase/storage';
import { firebaseApp } from 'firebaseConfig';
import Dropzone, { FileRejection } from 'react-dropzone';
import { toast } from 'react-toastify';
import API from 'api/API';
import { SetDraftResponse } from 'api/Responses/SetResponses';
import { FileUploadRoot } from 'components/FileUpload/FileUpload.styles';
import PageSpinner from 'components/PageSpinner/PageSpinner';
import { TrayFileUploader } from 'components/TrayDetails/TrayDetails.styles';
import { useLanguage } from 'context/AppContext';
import { draftActions, SetAction } from 'reducers/DraftReducer/draftActions';
import { Role, userRolesIndex } from 'store/user/auth0.helpers';
import { useUser } from 'store/user/useUser';
import { isInMaxKelsenOrg } from 'utils/helpers/permissions.helpers';
import * as Styles from './Table.styles';

interface SetManualProps {
  set: SetDraftResponse;
  dispatchSet: Dispatch<SetAction>;
  disabled: boolean;
}

interface SetFileManualProps {
  region: string | null;
  link: string | null;
  filename: string | null;
}

const SetManual: FC<SetManualProps> = ({ set, dispatchSet, disabled }) => {
  const { code } = set;
  const [language] = useLanguage();
  const user = useUser();
  const [loading, setLoading] = useState(false);
  const [files, setFiles] = useState<(SetFileManualProps | null)[]>([]);

  const ausManualFilename = set.set_manuals.manual_english;
  const jpnManualFilename = set.set_manuals.manual_japanese;
  const isMkSuperUser = useMemo(
    () => user.isAuthenticated && user[userRolesIndex].includes(Role.MkSystemAdmin) && isInMaxKelsenOrg(user),
    [user],
  );

  useEffect(() => {
    const getFileData = async (region: string, filename?: string): Promise<SetFileManualProps | null> => {
      try {
        if (!filename) return null;

        const downloadLinkPromise = getDownloadURL(ref(getStorage(firebaseApp), filename));
        const metadataPromise = getMetadata(ref(getStorage(firebaseApp), filename));
        const [downloadLink, metadata] = await Promise.all([downloadLinkPromise, metadataPromise]);

        return region && downloadLink && metadata.name
          ? { region: region, link: downloadLink, filename: metadata.name }
          : null;
      } catch (error) {
        API.errorHandler(error, language.SetTable.inspectionManualError + filename);
        return null;
      }
    };

    const getFiles = async (): Promise<void> => {
      setLoading(true);

      const ausFile = await getFileData('AUS', ausManualFilename);
      const jpnFile = await getFileData('JPN', jpnManualFilename);

      setFiles([ausFile, jpnFile]);

      setLoading(false);
    };

    getFiles();
  }, [set, ausManualFilename, jpnManualFilename, language.SetTable.inspectionManualError]);

  const onDrop = useCallback(
    async (acceptedFiles: File[], error: FileRejection[]) => {
      // If any errors exist, log them
      if (error.length > 0) {
        toast.error(error[0].errors[0].message);
      }
      // Otherwise, upload the requested image
      else {
        setLoading(true);
        try {
          // Post the image to the backend
          const response = await API.asset.putAssetV2SetsAssetSetIdPost({
            setId: code,
            assetType: 'set_manual',
            region: set?.region === 'AUS' ? 'AUS' : 'JPN',
            bodyPutAssetV2SetsAssetSetIdPost: { file: acceptedFiles[0] },
          });
          // Reflect changes in the frontend
          dispatchSet(draftActions.setManuals(response.gs_uri, set?.region === 'AUS' ? 'AUS' : 'JPN'));
          // Log successful upload
          // toast.success(language.AddModal.Tray.uploadSuccess);
        } catch (error) {
          API.errorHandler(error);
        } finally {
          setLoading(false);
        }
      }
    },
    [code, dispatchSet, set],
  );

  return (
    <Styles.SetManual text={language.SetTable.inspectionManual}>
      {loading ? (
        <PageSpinner />
      ) : (
        <Styles.SetManualContainer>
          <Styles.SetManualList>
            <Styles.SetManualItem>
              {files.every(file => file === null)
                ? language.SetTable.inspectionManualNoFile
                : files.map(file => {
                    const isValidToShowFile = file && (isMkSuperUser || set.region === file.region);
                    if (!isValidToShowFile) {
                      return null;
                    }

                    return (
                      <Styles.SetManualLink key={file.filename} href={file.link ?? ''} target="_blank" rel="noreferrer">
                        {file.filename}
                      </Styles.SetManualLink>
                    );
                  })}
            </Styles.SetManualItem>
          </Styles.SetManualList>
          {!disabled ? (
            <Styles.DropzoneContainer>
              <Dropzone onDrop={onDrop} maxFiles={1} disabled={disabled}>
                {({ getRootProps, getInputProps }) => (
                  <TrayFileUploader>
                    <FileUploadRoot {...getRootProps()}>
                      <input {...getInputProps()} />
                      <p>{language.SetTable.inspectionManualUpload}</p>
                    </FileUploadRoot>
                  </TrayFileUploader>
                )}
              </Dropzone>
            </Styles.DropzoneContainer>
          ) : null}
        </Styles.SetManualContainer>
      )}
    </Styles.SetManual>
  );
};

export default SetManual;
